import React, { useRef, useEffect, useState } from "react";
import { Switch, Route, Redirect } from "react-router-dom";

// core components
import SuperSidebar from "./nav/SuperSidebar";
import SuperTopbar from "./nav/SuperTopbar";

import routes from "routes.js";

import EditPromotion from "superAdminViews/ride/addPromotion/EditPromotion";
import EditShopPromotion from "superAdminViews/restaurant/promotions/EditShopPromotion";
import AddShopPromotion from "superAdminViews/restaurant/promotions/AddShopPromotion";
import EditUser from "superAdminViews/users/EditUser";
import EditAdmin from "superAdminViews/admins/addAdmin/EditAdmin";
import AddPromotion from "superAdminViews/ride/addPromotion/AddPromotion";
import EditDriver from "superAdminViews/ride/drivers/EditDriver";
import AddAdmin from "superAdminViews/admins/addAdmin/AddAdmin";
import AddAdvertisement from "superAdminViews/advertisements/addAdvertisement/AddAdvertisement";
import SignupDriver from "superAdminViews/ride/addDriver/SignupDriver";
import ShopVerifications from "superAdminViews/restaurant/verifications/ShopVerifications";
import AddDriver from "superAdminViews/ride/addDriver/AddDriver";
import Verifications from "superAdminViews/ride/verifications/Verifications";
import SuperAddShop from "superAdminViews/restaurant/addShop/SuperAddShop";
import AddCar from "superAdminViews/ride/addCar/AddCar";
import EditCar from "superAdminViews/ride/addCar/EditCar";
import EditAdvertisement from "superAdminViews/advertisements/addAdvertisement/EditAdvertisement";
import TripHistory from "superAdminViews/ride/trips/Trips";
import ShopPage from "superAdminViews/restaurant/shopPage/ShopPage";

// Permissions are written once at login and read on every route check.
// Parsing defensively because a half-cleared localStorage used to take
// the whole panel down with a SyntaxError rather than just hiding links.
const getPermissions = () => {
  try {
    const stored = JSON.parse(window.localStorage.getItem("permissions"));
    return Array.isArray(stored) ? stored : [];
  } catch (error) {
    return [];
  }
};

const isAllowed = (route, permissions) =>
  !route.permissionName || permissions.indexOf(route.permissionName) !== -1;

export default function SuperAdmin(props) {
  const pathname = props.location.pathname;

  const [pageTitle, setPageTitle] = useState("");
  useEffect(() => {
    const routeName = pathname.split("/")[2];
    const titleName = routeName
      ? routeName
          .replace(/([A-Z])/g, " $1")
          .replace(/_/g, " ")
          .toLowerCase()
      : "";
    const titleFinalName = titleName.charAt(0).toUpperCase() + titleName.slice(1);
    setPageTitle(titleFinalName);
    document.title = titleFinalName ? `Spark - ${titleFinalName}` : "Spark";
  }, [pathname]);

  const scrollRef = useRef(null);
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollIntoView();
  }, [pathname]);

  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => {
    const resizeFunction = () => {
      if (window.innerWidth >= 960) {
        setMobileOpen(false);
      }
    };
    window.addEventListener("resize", resizeFunction);
    return () => window.removeEventListener("resize", resizeFunction);
  }, []);

  const permissions = getPermissions();

  const getRoutes = (routeSet) =>
    routeSet.map((prop, key) => {
      if (prop.collapse) {
        return getRoutes(prop.views);
      }
      if (prop.layout !== "/superAdmin" || !isAllowed(prop, permissions)) {
        return null;
      }
      return (
        <Route
          path={prop.layout + prop.path}
          render={() => <prop.component type={props.type} {...prop.componentProps} />}
          key={key}
        />
      );
    });

  // The sidebar gets the same tree, minus anything this admin cannot
  // open — and minus any group that ends up empty once its children have
  // been filtered, which previously left a heading with nothing under it.
  const navRoutes = routes
    .filter((route) => route.layout === "/superAdmin")
    .map((route) => {
      if (!route.collapse) return isAllowed(route, permissions) ? route : null;
      const views = route.views.filter((child) => isAllowed(child, permissions));
      return views.length ? { ...route, views } : null;
    })
    .filter(Boolean);

  return (
    <div className="superAppShell">
      <SuperSidebar
        routes={navRoutes}
        pathname={pathname}
        mobileOpen={mobileOpen}
        onCloseMobile={() => setMobileOpen(false)}
      />
      <div className="superAppMain">
        <SuperTopbar title={pageTitle} onOpenMobile={() => setMobileOpen(true)} />
        <div className="superAppContent">
          <div className="superAppScrollTop" ref={scrollRef}></div>
          <Switch>
            {getRoutes(routes)}
            <Route path="/superAdmin/addAdmin" render={() => <AddAdmin />} />
            <Route
              path="/superAdmin/addAdvertisement"
              render={() => <AddAdvertisement />}
            />
            <Route
              path="/superAdmin/shopVerifications"
              render={() => <ShopVerifications />}
            />
            <Route path="/superAdmin/addShop" render={() => <SuperAddShop />} />
            <Route path="/superAdmin/addPromotion" render={() => <AddPromotion />} />
            <Route path="/superAdmin/shopAddPromotion" render={() => <AddShopPromotion />} />
            <Route path="/superAdmin/signupDriver" render={() => <SignupDriver />} />
            <Route path="/superAdmin/addDriver" render={() => <AddDriver />} />
            <Route path="/superAdmin/verifications" render={() => <Verifications />} />
            <Route path="/superAdmin/addVehicle" exact={true} render={() => <AddCar />} />
            <Route path="/superAdmin/editPromotion/:id" component={EditPromotion} />
            <Route path="/superAdmin/editShopPromotion/:id" component={EditShopPromotion} />
            <Route path="/superAdmin/editUser/:id" component={EditUser} />
            <Route path="/superAdmin/editAdmin/:id" component={EditAdmin} />
            <Route path="/superAdmin/editDriver/:id" component={EditDriver} />
            <Route
              path="/superAdmin/editVehicle/:carId"
              render={(routeProps) => <EditCar carId={routeProps.match.params.carId} />}
            />
            <Route path="/superAdmin/editAdvertisement/:id" component={EditAdvertisement} />
            <Route
              path="/superAdmin/tripHistory/:driverName/:driverPhoneNumber"
              render={(routeProps) => (
                <TripHistory
                  driverName={routeProps.match.params.driverName}
                  driverPhoneNumber={routeProps.match.params.driverPhoneNumber}
                  history={true}
                />
              )}
            />
            <Route path="/superAdmin/:shopName/:id" component={ShopPage} />
            <Redirect from="/superAdmin" to="/superAdmin/dashboard" />
          </Switch>
        </div>
      </div>
    </div>
  );
}
